import { SignJWT, jwtVerify } from 'jose';
import bcrypt from 'bcryptjs';

const JWT_SECRET = new TextEncoder().encode(
  process.env.JWT_SECRET || 'attendance_super_secret_jwt_key_2026_modern_hr'
);

export const AUTH_COOKIE_NAME = 'auth_token';

// Available application modules for RBAC
export const APP_MODULES = [
  { id: 'dashboard', name: 'Dashboard', path: '/dashboard' },
  { id: 'employees', name: 'Employees', path: '/employees' },
  { id: 'documents', name: 'Documents', path: '/documents' },
  { id: 'payroll', name: 'Payroll', path: '/payroll' },
  { id: 'payslips', name: 'Payslips', path: '/payroll/payslips' },
  { id: 'salary-history', name: 'Salary History', path: '/payroll/salary-history' },
  { id: 'companies', name: 'Companies', path: '/companies' },
  { id: 'departments', name: 'Departments', path: '/departments' },
  { id: 'roles', name: 'Roles', path: '/designations' },
  { id: 'users', name: 'User Management', path: '/users' },
] as const;

export type ModuleId = typeof APP_MODULES[number]['id'];

// Default role permissions
export const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  ADMIN: ['*'], // Super Admin can access everything
  HR: ['dashboard', 'employees', 'documents', 'departments', 'roles', 'companies'],
  ACCOUNTS: ['dashboard', 'payroll', 'payslips', 'salary-history', 'companies'],
  VIEWER: ['dashboard', 'employees'],
  SERVICE: ['*'], // Direct API integration
  TEST: ['dashboard', 'employees', 'documents', 'payroll'],
};

export interface AuthTokenPayload {
  userId: number;
  identifier: string;
  name: string;
  email: string | null;
  username: string | null;
  employeeId: string | null;
  role: string;
  accountType: string;
  permissions: string[];
}

// Password utilities
export async function hashPassword(plainText: string): Promise<string> {
  const salt = await bcrypt.genSalt(10);
  return bcrypt.hash(plainText, salt);
}

export async function verifyPassword(plainText: string, hashed: string): Promise<boolean> {
  return bcrypt.compare(plainText, hashed);
}

export const SESSION_DURATION_SECONDS = 30 * 60; // 30 minutes in seconds

// JWT Token creation
export async function createAuthToken(payload: AuthTokenPayload): Promise<string> {
  return new SignJWT({ ...payload })
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime('30m') // 30 minutes rolling session
    .sign(JWT_SECRET);
}

// JWT Token verification
export async function verifyAuthToken(token: string): Promise<AuthTokenPayload | null> {
  try {
    const { payload } = await jwtVerify(token, JWT_SECRET);
    return payload as unknown as AuthTokenPayload;
  } catch (error) {
    return null;
  }
}

// Check if user has permission for a specific module
export function hasModuleAccess(userPermissions: string[], role: string, moduleId: string): boolean {
  if (role === 'ADMIN' || userPermissions.includes('*')) {
    return true;
  }
  return userPermissions.includes(moduleId);
}
